import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Shield, Code, Scan, Calculator, Download, Zap, Lock, Globe } from 'lucide-react';

export default function Landing() {
  const services = [
    {
      icon: Code,
      title: 'Custom Software',
      description: 'Bespoke software solutions tailored to your exact business needs.',
      link: '/estimator',
      color: 'blue'
    },
    {
      icon: Shield,
      title: 'Security Services',
      description: 'Protect your digital assets with our comprehensive OpSec solutions.',
      link: '/security',
      color: 'green'
    },
    {
      icon: Scan,
      title: 'Virus Scanner',
      description: 'Free tool to check files for malware and security threats.',
      link: '/scanner',
      color: 'purple'
    },
    {
      icon: Download,
      title: 'Free Software',
      description: 'Download working sample software and games at no cost.',
      link: '/software',
      color: 'orange'
    }
  ];

  const features = [
    { icon: Zap, title: 'Lightning Fast', description: 'Optimized for performance' },
    { icon: Lock, title: 'Secure by Design', description: 'Security-first approach' },
    { icon: Globe, title: 'Scalable', description: 'Grows with your business' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center stagger-children">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
              <span className="w-2 h-2 rounded-full bg-green-400 pulse-indicator" />
              <span className="text-sm text-blue-300 font-mono">Detroit-Based Software Forge</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 font-['Outfit']">
              <span className="text-white">Build.</span>{' '}
              <span className="text-gradient">Protect.</span>{' '}
              <span className="text-white">Deploy.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
              Custom software solutions with enterprise-grade security. From concept to deployment, we build digital fortresses.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/estimator">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 btn-glow group" data-testid="get-estimate-btn">
                  Get Free Estimate
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/software">
                <Button size="lg" variant="outline" className="border-white/20 hover:bg-white/5" data-testid="browse-software-btn">
                  Browse Free Software
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent z-10" />
            <img
              src="https://images.unsplash.com/photo-1710957987034-cea509422852?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGN5YmVyJTIwc2VjdXJpdHklMjBuZXR3b3JrJTIwZGF0YSUyMGZsb3clMjBibHVlJTIwbmVvbiUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MjU4MDU0Nnww&ixlib=rb-4.1.0&q=85"
              alt="Cyber Network"
              className="w-full h-64 md:h-96 object-cover rounded-xl border border-white/10"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4 font-['Outfit']">
              Our Services
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Comprehensive software and security solutions for modern businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link key={index} to={service.link}>
                <Card className="bg-gray-900/50 border-white/10 hover:border-blue-500/30 transition-colors h-full card-hover group">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg bg-${service.color}-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <service.icon className={`w-6 h-6 text-${service.color}-400`} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{service.title}</h3>
                    <p className="text-gray-400 text-sm">{service.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6 font-['Outfit']">
                Why Choose <span className="text-gradient">Gibson Stratum</span>?
              </h2>
              <p className="text-gray-400 mb-8">
                We combine cutting-edge technology with Detroit craftsmanship. Every line of code is written with security and performance in mind.
              </p>
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{feature.title}</h4>
                      <p className="text-gray-400 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1684488624316-774ea1824d97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGNvZGluZyUyMGluJTIwZGFyayUyMHJvb20lMjBuZW9uJTIwbGlnaHRzJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3NzI1ODA1NDh8MA&ixlib=rb-4.1.0&q=85"
                alt="Developer"
                className="rounded-xl border border-white/10"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 to-transparent rounded-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="glass rounded-2xl p-12 border border-white/10">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4 font-['Outfit']">
              Ready to Build Something Great?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Get a free project estimate with AI-powered design structure preview. No commitment required.
            </p>
            <Link to="/estimator">
              <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8 glow-green" data-testid="cta-estimate-btn">
                Start Your Project
                <Calculator className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
